<template>
  <div ref="rootRef" class="gsearch">
    <!-- Поле поиска в топбаре -->
    <div
      class="gsearch__field"
      :class="{ 'gsearch__field--active': isFocused }"
      @click="inputRef?.focus()"
    >
      <SearchIcon :size="18" class="gsearch__field-icon" />
      <input
        ref="inputRef"
        v-model="queryModel"
        type="search"
        enterkeyhint="search"
        class="gsearch__input"
        :placeholder="t('common.actions.globalSearch')"
        autocomplete="off"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @keydown="onKeydown"
      />
      <button
        v-if="isFocused"
        type="button"
        class="gsearch__esc-chip"
        :aria-label="t('common.actions.close')"
        @mousedown.prevent="closeAndReset"
      >
        <span class="gsearch__esc-text">esc</span>
        <XIcon :size="20" class="gsearch__esc-icon" aria-hidden="true" />
      </button>
    </div>

    <!-- Дропдаун под полем (не модалка) -->
    <Transition name="gsearch-pop">
      <div v-if="isFocused" class="gsearch__panel" @mousedown.prevent>
        <div class="gsearch__body">
          <!-- РЕЖИМ ВВОДА: подсказки от бэкенда -->
          <template v-if="hasQuery">
            <div v-if="store.isLoading" class="gsearch__state">
              <Loader2Icon :size="16" class="gsearch__spin" />
              <span>{{ t('common.globalSearch.loading') }}</span>
            </div>

            <div v-else-if="store.error" class="gsearch__state gsearch__state--error">
              {{ t('common.globalSearch.error') }}
            </div>

            <div v-else-if="!store.hasResults" class="gsearch__state gsearch__state--muted">
              {{ t('common.globalSearch.empty') }}
            </div>

            <template v-else>
              <template v-for="group in store.groups" :key="group.type">
                <div class="gsearch__gh">{{ t(groupLabel(group.type)) }} · {{ group.total }}</div>
                <div
                  v-for="hit in group.hits"
                  :key="hit.type + ':' + hit.entityId"
                  class="gsearch__item"
                  :class="{ 'gsearch__item--active': isActive(hit) }"
                  @mouseenter="setActive(hit)"
                  @click="goTo(hit)"
                >
                  <span class="gsearch__tile" :class="tileClass(hit.type)">
                    <component :is="groupIcon(hit.type)" :size="16" />
                  </span>
                  <span class="gsearch__mid">
                    <span class="gsearch__title">
                      <template v-for="(part, i) in highlight(hit.title)" :key="i">
                        <span v-if="part.hl" class="gsearch__hl">{{ part.s }}</span>
                        <template v-else>{{ part.s }}</template>
                      </template>
                    </span>
                    <span v-if="hit.subtitle" class="gsearch__sub">{{ hit.subtitle }}</span>
                  </span>
                  <CornerDownLeftIcon v-if="isActive(hit)" :size="15" class="gsearch__enter" />
                </div>
              </template>
            </template>
          </template>

          <!-- РЕЖИМ ФОКУСА (пустой запрос): недавнее + быстрые действия -->
          <template v-else>
            <template v-if="store.recent.length">
              <div class="gsearch__gh">{{ t('common.globalSearch.recent') }}</div>
              <div
                v-for="hit in store.recent"
                :key="hit.type + ':' + hit.entityId"
                class="gsearch__item"
                :class="{ 'gsearch__item--active': isActive(hit) }"
                @mouseenter="setActive(hit)"
                @click="goTo(hit)"
              >
                <span class="gsearch__tile" :class="tileClass(hit.type)">
                  <component :is="groupIcon(hit.type)" :size="16" />
                </span>
                <span class="gsearch__mid">
                  <span class="gsearch__title">{{ hit.title }}</span>
                  <span v-if="hit.subtitle" class="gsearch__sub">{{ hit.subtitle }}</span>
                </span>
                <span class="gsearch__recent-actions">
                  <ClockIcon :size="15" class="gsearch__clock" />
                  <button
                    type="button"
                    class="gsearch__remove"
                    :aria-label="t('common.actions.remove')"
                    @click.stop="store.removeRecent(hit)"
                  >
                    <XIcon :size="14" />
                  </button>
                </span>
              </div>
            </template>

            <div class="gsearch__qa">
              <button type="button" class="gsearch__chip" @click="quickCreate('client')">
                <PlusIcon :size="14" />
                <span>{{ t('common.globalSearch.newClient') }}</span>
              </button>
              <!-- DEV-699. support_manager сделки не создаёт. -->
              <button
                v-if="canCreateDeals"
                type="button"
                class="gsearch__chip"
                @click="quickCreate('deal')"
              >
                <PlusIcon :size="14" />
                <span>{{ t('common.globalSearch.newDeal') }}</span>
              </button>
            </div>
          </template>
        </div>

        <!-- Подсказки по клавишам -->
        <div class="gsearch__foot">
          <span class="gsearch__hint"
            ><kbd>↑</kbd><kbd>↓</kbd>{{ t('common.globalSearch.navHint') }}</span
          >
          <span class="gsearch__hint"><kbd>↵</kbd>{{ t('common.globalSearch.openHint') }}</span>
          <span class="gsearch__hint"><kbd>esc</kbd>{{ t('common.actions.close') }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  SearchIcon,
  XIcon,
  UsersIcon,
  BriefcaseIcon,
  Building2Icon,
  Loader2Icon,
  ClockIcon,
  PlusIcon,
  CornerDownLeftIcon,
} from 'lucide-vue-next'
import { useGlobalSearchStore } from '../stores/globalSearch.store'
import { usePermissions } from '@/features/auth'
import type { SearchEntityType, SearchHit } from '../types/search.types'

/** Минимум символов для запроса к бэкенду (синхронно со стором). */
const MIN_CHARS = 2

const { t } = useI18n()
const router = useRouter()
const store = useGlobalSearchStore()
// DEV-699. support_manager (и marketer) не создаёт сделки — чип скрыт.
const { canCreateDeals } = usePermissions()

const rootRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const isFocused = ref(false)

const queryModel = computed({
  get: () => store.query,
  set: (value: string) => store.search(value),
})

const hasQuery = computed(() => store.query.trim().length >= MIN_CHARS)

// Базовые пути карточек CRM (под /dashboard).
//
// Карты ниже Partial по замыслу: user и article в глобальную выдачу не входят
// (dto.AllEntityTypes на бэкенде), они достижимы только через SearchEntity —
// поэтому подписи, иконки и маршруты для них тут не нужны, а каждое чтение и так
// прикрыто фолбэком.
const ENTITY_BASE: Partial<Record<SearchEntityType, string>> = {
  SEARCH_ENTITY_TYPE_CLIENT: '/dashboard/clients',
  SEARCH_ENTITY_TYPE_DEAL: '/dashboard/deals',
  SEARCH_ENTITY_TYPE_PROPERTY: '/dashboard/properties',
  // DEV-478/DEV-475: агенты и компании-партнёры в глобальном поиске.
  SEARCH_ENTITY_TYPE_AGENT: '/dashboard/agents',
  SEARCH_ENTITY_TYPE_PARTNER_COMPANY: '/dashboard/partner-companies',
}
const GROUP_LABEL: Partial<Record<SearchEntityType, string>> = {
  SEARCH_ENTITY_TYPE_CLIENT: 'common.globalSearch.groups.clients',
  SEARCH_ENTITY_TYPE_DEAL: 'common.globalSearch.groups.deals',
  SEARCH_ENTITY_TYPE_PROPERTY: 'common.globalSearch.groups.properties',
  SEARCH_ENTITY_TYPE_AGENT: 'common.navigation.agents',
  SEARCH_ENTITY_TYPE_PARTNER_COMPANY: 'partnerCompanies.title',
}
const GROUP_ICON: Partial<Record<SearchEntityType, unknown>> = {
  SEARCH_ENTITY_TYPE_CLIENT: UsersIcon,
  SEARCH_ENTITY_TYPE_DEAL: BriefcaseIcon,
  SEARCH_ENTITY_TYPE_PROPERTY: Building2Icon,
  SEARCH_ENTITY_TYPE_AGENT: UsersIcon,
  SEARCH_ENTITY_TYPE_PARTNER_COMPANY: Building2Icon,
}
const TILE_CLASS: Partial<Record<SearchEntityType, string>> = {
  SEARCH_ENTITY_TYPE_CLIENT: 'gsearch__tile--client',
  SEARCH_ENTITY_TYPE_DEAL: 'gsearch__tile--deal',
  SEARCH_ENTITY_TYPE_PROPERTY: 'gsearch__tile--property',
  SEARCH_ENTITY_TYPE_AGENT: 'gsearch__tile--client',
  SEARCH_ENTITY_TYPE_PARTNER_COMPANY: 'gsearch__tile--property',
}

const groupLabel = (type: SearchEntityType) => GROUP_LABEL[type] ?? type
const groupIcon = (type: SearchEntityType) => GROUP_ICON[type] ?? SearchIcon
const tileClass = (type: SearchEntityType) => TILE_CLASS[type] ?? 'gsearch__tile--client'

// Список для навигации стрелками: результаты при вводе, иначе — недавнее.
const navList = computed<SearchHit[]>(() =>
  hasQuery.value ? store.groups.flatMap((g) => g.hits) : store.recent,
)
const activeIndex = ref(0)
const activeHit = computed<SearchHit | undefined>(() => navList.value[activeIndex.value])
const isActive = (hit: SearchHit) => hit === activeHit.value

const setActive = (hit: SearchHit) => {
  const idx = navList.value.indexOf(hit)
  if (idx >= 0) activeIndex.value = idx
}

// Подсветка совпадения запроса в заголовке (регистронезависимо).
const highlight = (text: string): Array<{ s: string; hl: boolean }> => {
  const q = store.query.trim()
  if (!q) return [{ s: text, hl: false }]

  const lowerText = text.toLowerCase()
  const lowerQ = q.toLowerCase()
  const parts: Array<{ s: string; hl: boolean }> = []
  let pos = 0

  while (pos < text.length) {
    const idx = lowerText.indexOf(lowerQ, pos)
    if (idx === -1) {
      parts.push({ s: text.slice(pos), hl: false })
      break
    }
    if (idx > pos) parts.push({ s: text.slice(pos, idx), hl: false })
    parts.push({ s: text.slice(idx, idx + lowerQ.length), hl: true })
    pos = idx + lowerQ.length
  }

  return parts
}

const resolvePath = (hit: SearchHit): string => {
  const base = ENTITY_BASE[hit.type]
  if (base) return `${base}/${hit.entityId}`

  return hit.route ? `/dashboard${hit.route}` : '/dashboard'
}

const goTo = (hit: SearchHit) => {
  store.addRecent(hit)
  router.push(resolvePath(hit))
  store.clear()
  inputRef.value?.blur()
}

const quickCreate = (kind: 'client' | 'deal') => {
  router.push(kind === 'client' ? '/dashboard/clients/create' : '/dashboard/deals/create')
  store.clear()
  inputRef.value?.blur()
}

const closeAndReset = () => {
  store.clear()
  inputRef.value?.blur()
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeAndReset()

    return
  }

  const n = navList.value.length
  if (n === 0) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % n
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value - 1 + n) % n
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const hit = navList.value[activeIndex.value]
    if (hit) goTo(hit)
  }
}

// Сброс выделения при смене списка (новые результаты / переход в режим недавнего).
watch(navList, () => {
  activeIndex.value = 0
})

// Фокус инпута снаружи (хоткей Cmd/Ctrl+K в DashboardLayout).
const focus = () => inputRef.value?.focus()
defineExpose({ focus })

onUnmounted(() => {
  store.clear()
})
</script>

<style scoped>
.gsearch {
  position: relative;
}

/* Поле */
.gsearch__field {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 320px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid transparent;
  border-radius: var(--radius-md, 10px);
  background: var(--color-bg-muted);
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.gsearch__field--active {
  background: var(--color-surface, #ffffff);
  border-color: var(--color-primary, #4f46e5);
  box-shadow: var(--shadow-focus, 0 0 0 3px rgba(79, 70, 229, 0.12));
}

.gsearch__field-icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.gsearch__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family-sans);
  font-size: 0.875rem;
  color: var(--color-text-primary);
}

.gsearch__input::placeholder {
  color: var(--color-text-muted);
}

.gsearch__input::-webkit-search-cancel-button {
  display: none;
}

.gsearch__esc-chip {
  flex-shrink: 0;
  padding: 2px 7px;
  border: 1px solid var(--color-border-strong, #e4e4e7);
  border-radius: var(--radius-sm, 6px);
  background: var(--color-bg-muted, #f4f4f5);
  color: var(--color-text-muted, #a1a1aa);
  font-family: var(--font-family-sans);
  font-size: 0.6875rem;
  line-height: 1.2;
  cursor: pointer;
  transition: color 0.15s ease;
}

.gsearch__esc-chip:hover {
  color: var(--color-text-primary);
}

.gsearch__esc-icon {
  display: none;
}

/* Панель */
.gsearch__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 70vh;
  background: var(--color-background-elevated, #ffffff);
  border: 1px solid var(--color-border-light, #ecedf1);
  border-radius: var(--radius-md, 10px);
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  z-index: 50;
  overflow: hidden;
}

.gsearch__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px;
}

/* Состояния */
.gsearch__state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 12px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.gsearch__state--muted {
  color: var(--color-text-muted);
}

.gsearch__state--error {
  color: var(--color-danger);
}

.gsearch__spin {
  animation: gsearch-spin 0.8s linear infinite;
}

@keyframes gsearch-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Заголовок группы */
.gsearch__gh {
  padding: 9px 10px 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-muted);
}

/* Элемент */
.gsearch__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 7px 10px;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
}

.gsearch__item--active {
  background: var(--color-primary-tint, #eef2ff);
}

.gsearch__tile {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md, 8px);
}

.gsearch__tile--client {
  background: var(--color-bg-muted, #f4f4f5);
  color: var(--color-text-secondary, #52525b);
}

.gsearch__tile--deal {
  background: var(--color-success-tint, #ecfdf5);
  color: var(--color-success, #10b981);
}

.gsearch__tile--property {
  background: var(--color-info-tint, #eff6ff);
  color: var(--color-info, #0ea5e9);
}

.gsearch__mid {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gsearch__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gsearch__hl {
  color: var(--color-primary, #4f46e5);
  font-weight: 600;
}

.gsearch__sub {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gsearch__enter {
  flex-shrink: 0;
  color: var(--color-primary, #4f46e5);
}

.gsearch__recent-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-muted);
}

.gsearch__remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: var(--radius-sm, 6px);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.gsearch__remove:hover {
  background: var(--color-border);
  color: var(--color-text-primary);
}

/* Быстрые действия */
.gsearch__qa {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 10px 6px;
}

.gsearch__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--color-border-strong, #e4e4e7);
  border-radius: var(--radius-full, 9999px);
  background: var(--color-surface, #ffffff);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: 0.8125rem;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
}

.gsearch__chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Футер с хоткеями */
.gsearch__foot {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  padding: 8px 12px;
  border-top: 1px solid var(--color-border-light, #ecedf1);
  background: var(--color-background-soft, #f8fafc);
}

.gsearch__hint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.6875rem;
  color: var(--color-text-muted);
}

.gsearch__hint kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 17px;
  padding: 0 4px;
  border: 1px solid var(--color-border-strong, #e4e4e7);
  border-radius: var(--radius-sm, 6px);
  background: var(--color-surface, #ffffff);
  font-family: var(--font-family-sans);
  font-size: 0.625rem;
  line-height: 1;
}

/* Анимация */
.gsearch-pop-enter-active,
.gsearch-pop-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.gsearch-pop-enter-from,
.gsearch-pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 1024px) {
  .gsearch__field {
    width: 240px;
  }

  .gsearch__panel {
    width: 380px;
  }
}

@media (max-width: 860px) {
  .gsearch__field {
    width: 190px;
  }
}

/* DEV-2233: на телефоне поле поиска схлопнуто в тап-цель 44×44 — иначе оно
   наезжало на крошки, а иконки звонка, уведомлений и аватара выталкивало за экран.
   По тапу поле разворачивается поверх шапки, подсказки — во всю ширину экрана. */
@media (max-width: 600px) {
  .gsearch__field {
    width: 44px;
    height: 44px;
    padding: 0;
    justify-content: center;
    cursor: pointer;
  }

  /* input не скрываем через display: none — иначе фокус по тапу не поставить */
  .gsearch__input {
    flex: 0 0 0;
    width: 0;
    opacity: 0;
  }

  .gsearch__field--active {
    position: fixed;
    left: 8px;
    right: 8px;
    top: 10px;
    width: auto;
    padding: 0 12px;
    justify-content: flex-start;
    z-index: 60;
  }

  .gsearch__field--active .gsearch__input {
    flex: 1;
    width: auto;
    opacity: 1;
  }

  .gsearch__panel {
    position: fixed;
    left: 8px;
    right: 8px;
    top: calc(var(--app-header-height, 64px) + var(--impersonation-banner-height, 0px) + 6px);
    width: auto;
    max-height: calc(
      100dvh - var(--app-header-height, 64px) - var(--impersonation-banner-height, 0px) - 20px
    );
  }

  /* Быстрые действия в панели — тап-цели, а не 30px полоски */
  .gsearch__chip {
    min-height: 44px;
  }
}

/* DEV-2501. На тач-экране клавиш нет: вместо чипа «esc» в 11px — крестик 44×44,
   подсказок ↑↓↵ нет. В конце файла — после базовых правил чипа и подвала. */
@media (pointer: coarse) {
  .gsearch__esc-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-right: calc(-1 * var(--spacing-sm, 0.5rem));
    padding: 0;
    border: none;
    background: none;
    color: var(--color-text-secondary);
  }

  .gsearch__esc-text {
    display: none;
  }

  .gsearch__esc-icon {
    display: block;
  }

  .gsearch__foot {
    display: none;
  }
}
</style>
